import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';
import { Helmet } from 'react-helmet';

import articleExtractor from './extractors/article';
import pageExtractor from './extractors/page';
import {
  ExtractorsMapObject,
  PageSchemaProps,
  PageSchemaQuery,
} from './models';

const extractorsMap: ExtractorsMapObject = {
  Article: articleExtractor,
  default: pageExtractor,
};

const PageSchema: FC<PageSchemaProps> = ({
  type,
  name,
  path,
  data,
  locale,
}) => {
  const {
    allContentstackSettings: { nodes: settingsNodes },
    allContentstackHeader: { nodes: headerNodes },
  } = useStaticQuery<PageSchemaQuery>(
    graphql`
      query PageSchema {
        allContentstackSettings {
          nodes {
            siteUrl: site_url
            siteName: site_name
            defaultLocale: default_locale
            tags
            publishDetails: publish_details {
              locale
            }
          }
        }
        allContentstackHeader {
          nodes {
            logo {
              largeImage: large_image {
                url
              }
            }
            tags
            publishDetails: publish_details {
              locale
            }
          }
        }
      }
    `
  );

  const settings = settingsNodes.find(
    ({ tags, publishDetails: { locale: publishLocale } }) =>
      tags.includes(process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string) &&
      publishLocale === locale
  );

  const logo = headerNodes.find(
    ({ tags, publishDetails: { locale: publishLocale } }) =>
      tags.includes(process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string) &&
      publishLocale === locale
  );

  if (!settings) {
    console.error(
      `Missing settings for PageSchema with tag ${process.env.GATSBY_CONTENTSTACK_BRAND_TAG}`
    );

    return null;
  }

  const { siteUrl, siteName, defaultLocale } = settings;

  const brandInformation = {
    lang: defaultLocale,
    siteName,
    siteUrl,
    logo: logo?.logo.largeImage.url,
  };

  const pageHref = path
    ? `${brandInformation.siteUrl}${path}`
    : brandInformation.siteUrl;

  const extractor = extractorsMap[type] || extractorsMap.default;

  const jsonLd = {
    name: name || brandInformation.siteName,
    ...{
      '@context': 'https://schema.org',
      '@type': type,
    },
    ...extractor(pageHref, data, brandInformation),
  };

  // Added key to script to prevent multirender on navigation
  return (
    <Helmet encodeSpecialCharacters={false}>
      <script type="application/ld+json" key="jsonLd">
        {JSON.stringify(jsonLd)}
      </script>
    </Helmet>
  );
};

export default PageSchema;
